/**
 * Terms of Service
 *
 * Public page — no auth required.
 */

export function Terms() {
	return (
		<div class="page legal-page">
			<header class="legal-header">
				<div class="container">
					<nav class="legal-nav">
						<a href="/" class="logo">
							<span class="logo-text">pxtxt</span>
						</a>
					</nav>
				</div>
			</header>

			<main class="legal-main">
				<div class="container legal-container">
					<h1>Terms of Service</h1>
					<p class="legal-meta">Last updated: March 28, 2026</p>

					<section>
						<h2>1. Service Description</h2>
						<p>
							pxtxt ("the Service") is a professional representation platform operated by CaliKo LLC ("we", "us", "our").
							The Service builds canonical understanding of professionals through AI-guided interviews, generates professional narratives,
							and provides AI-powered representation agents that engage with visitors on behalf of professionals.
						</p>
						<p>
							The Service uses artificial intelligence (Anthropic's Claude) to conduct interviews, extract structured claims,
							and represent professionals to visitors. Company context may be enriched through our BETA company intelligence platform.
						</p>
					</section>

					<section>
						<h2>2. Accounts and Eligibility</h2>
						<p>You must be at least 18 years old to use the Service.</p>
						<p>
							You are responsible for maintaining the security of your account credentials.
							Each person may maintain only one account. You must provide accurate information
							when creating your account and during interviews.
						</p>
					</section>

					<section>
						<h2>3. Subscription and Payment</h2>
						<p>The Service offers the following tiers:</p>
						<ul>
							<li><strong>Free</strong>: 1 interview session. Published profile with AI representation agent. Engagement insights. Resume generation. Data stored and exportable.</li>
							<li><strong>Pro ($12/month or $120/year)</strong>: Unlimited interview sessions. Full-depth professional narrative. Full engagement reports. Company intelligence via BETA. Tools (Opportunity Analysis). Custom URL.</li>
						</ul>
						<p>
							Pro subscriptions are billed monthly through Stripe. You may cancel at any time;
							access continues through the end of the current billing period. No refunds are provided
							for partial billing periods.
						</p>
						<p>
							We may change subscription pricing with 30 days' notice. Continued use after the notice period
							constitutes acceptance of the new pricing.
						</p>
					</section>

					<section>
						<h2>4. User Content and Data Ownership</h2>
						<p>
							<strong>You own your data.</strong> All professional information you provide through interviews,
							including claims, work history, skills, and voice preferences, remains your property.
						</p>
						<p>
							By using the Service, you grant us a limited license to use your content solely for
							the purpose of delivering the Service — including generating narratives, powering representation agents,
							and displaying public profiles. This license terminates when you delete your account.
						</p>
						<p>
							You certify that all information you provide is accurate and truthful to the best of your knowledge.
							You are responsible for the accuracy of claims made during interviews.
						</p>
						<p>
							You may export your canonical data in JSON format at any time. You may delete your account
							and all associated data at any time.
						</p>
					</section>

					<section>
						<h2>5. AI Representation Disclaimer</h2>
						<p>
							The representation agent generates responses based on information you have provided through interviews,
							enriched with publicly available company context. While we enforce anti-fabrication guardrails,
							the AI may occasionally produce responses that do not perfectly reflect your intent or experience.
						</p>
						<p>
							pxtxt does not independently verify all professional claims. The interview process structures
							and validates claims, and company context is enriched through BETA's public data sources,
							but the professional is the authoritative source of their own experience.
						</p>
						<p>
							Visitors interacting with your representation agent are informed that responses are AI-generated
							and based on information provided by the professional.
						</p>
					</section>

					<section>
						<h2>6. Company Intelligence</h2>
						<p>
							The Service may enrich your professional claims with company context from BETA,
							a company intelligence platform operated by CaliKo LLC. BETA sources data from
							public records and third-party providers. While we strive for accuracy, company
							information is provided as-is and may contain errors or be out of date.
						</p>
					</section>

					<section>
						<h2>7. Prohibited Conduct</h2>
						<p>You agree not to:</p>
						<ul>
							<li>Fabricate professional credentials or misrepresent your experience</li>
							<li>Impersonate another professional</li>
							<li>Use the Service for spam, harassment, or deceptive purposes</li>
							<li>Attempt to manipulate or game the representation agent</li>
							<li>Interfere with or disrupt the Service or its infrastructure</li>
							<li>Use the Service in violation of any applicable law</li>
						</ul>
					</section>

					<section>
						<h2>8. Intellectual Property</h2>
						<p>
							The Service, including its interview methodologies, representation algorithms,
							prompt engineering, and presentation formats, is owned by CaliKo LLC.
							The canonical data schema is published as an open standard.
						</p>
						<p>
							You retain ownership of all canonical data and claims you contribute.
							Third-party content (including BETA company data) belongs to its respective owners.
						</p>
					</section>

					<section>
						<h2>9. Limitation of Liability</h2>
						<p>
							The Service is provided "as is" without warranties of any kind, express or implied.
						</p>
						<p>
							CaliKo LLC is not liable for any hiring, business, or employment decisions made by
							third parties based on information presented through the Service.
						</p>
						<p>
							Our maximum aggregate liability to you for any claims arising from or related to
							the Service is limited to the total fees you have paid in the twelve (12) months
							preceding the claim.
						</p>
					</section>

					<section>
						<h2>10. Termination</h2>
						<p>
							Either party may terminate this agreement at any time. You may cancel your subscription
							and delete your account through the Service settings.
						</p>
						<p>
							Upon termination, your data will be available for export for 30 days, after which
							it will be permanently deleted from our systems.
						</p>
						<p>
							We reserve the right to suspend or terminate accounts that violate these terms.
						</p>
					</section>

					<section>
						<h2>11. Changes to Terms</h2>
						<p>
							We may update these terms from time to time. Material changes will be communicated
							with at least 30 days' notice via email. Continued use of the Service after the
							notice period constitutes acceptance of the revised terms.
						</p>
					</section>

					<section>
						<h2>12. Contact</h2>
						<p>
							For questions about these terms, contact us at <a href="mailto:legal@pxtxt.ai">legal@pxtxt.ai</a>.
						</p>
					</section>
				</div>
			</main>

			<Footer />
			<style>{legalStyles}</style>
		</div>
	);
}

function Footer() {
	return (
		<footer class="legal-footer">
			<div class="container">
				<div class="footer-content">
					<p class="t-caption">pxtxt — Professional representation with depth</p>
					<nav class="footer-links">
						<a href="/terms">Terms</a>
						<a href="/privacy">Privacy</a>
					</nav>
				</div>
			</div>
		</footer>
	);
}

const legalStyles = `
	.legal-page {
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		background: var(--color-bg);
	}

	.legal-header {
		padding: 1rem 0;
		background: var(--color-bg-elevated);
		border-bottom: 1px solid var(--color-border);
	}

	.legal-nav {
		display: flex;
		align-items: center;
	}

	.logo {
		text-decoration: none;
	}

	.logo-text {
		font-family: var(--font-display);
		font-size: 1.5rem;
		font-weight: 700;
		color: var(--color-title);
		letter-spacing: -0.02em;
	}

	.legal-main {
		flex: 1;
		padding: 3rem 0;
	}

	.legal-container {
		max-width: 720px;
		margin: 0 auto;
		padding: 0 1.5rem;
	}

	.legal-container h1 {
		font-family: var(--font-display);
		font-size: 2rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0 0 0.5rem;
	}

	.legal-meta {
		color: var(--color-text-muted);
		font-size: 0.875rem;
		margin-bottom: 2.5rem;
	}

	.legal-container section {
		margin-bottom: 2rem;
	}

	.legal-container h2 {
		font-family: var(--font-display);
		font-size: 1.25rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0 0 0.75rem;
	}

	.legal-container p {
		line-height: 1.7;
		color: var(--color-text);
		margin: 0 0 0.75rem;
	}

	.legal-container ul {
		padding-left: 1.5rem;
		margin: 0 0 0.75rem;
	}

	.legal-container li {
		line-height: 1.7;
		color: var(--color-text);
		margin-bottom: 0.375rem;
	}

	.legal-container a {
		color: var(--color-primary);
		text-decoration: none;
	}

	.legal-container a:hover {
		text-decoration: underline;
	}

	.legal-footer {
		padding: 2rem 0;
		border-top: 1px solid var(--color-border);
	}

	.footer-content {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.footer-links {
		display: flex;
		gap: 1.5rem;
	}

	.footer-links a {
		color: var(--color-text-muted);
		text-decoration: none;
		font-size: 0.875rem;
	}

	.footer-links a:hover {
		color: var(--color-text);
	}

	@media (max-width: 767px) {
		.legal-main {
			padding: 2rem 0;
		}

		.legal-container h1 {
			font-size: 1.5rem;
		}

		.footer-content {
			flex-direction: column;
			gap: 0.75rem;
			text-align: center;
		}
	}
`;
